import React, { ReactNode, ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

/** Route for non–client_view users redirected from `clientOnly` screens */
export const CLIENT_ONLY_RESTRICTED_PATH = "/client-only-restricted";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  // Add prop to mark routes as client-only
  clientOnly?: boolean;
}

// Define client-accessible routes
const CLIENT_ACCESSIBLE_ROUTES = [
  "/client-overview",
  "/accountant-status",
  "/client/queries",
  "/client/compliance",
  "/revenue",
  "/expense",
  "/cash-and-bank",
  "/books",
  "/reports", // simple reports area (ZOUR-22)
  // So client_view can load the “staff-only” explanation page and be redirected inside it
  "/client-only-restricted",
];

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  clientOnly = false,
}): ReactElement => {
  const {
    isAuthenticated,
    loading,
    user,
    isAdmin,
    hasPermission,
    isClientView,
  } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          <p className="text-body font-body text-subtext-color">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Client-only routes: only client_view may access; others go to explanation page
  if (clientOnly && !isClientView()) {
    return (
      <Navigate
        to={CLIENT_ONLY_RESTRICTED_PATH}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check if user is client_view and trying to access non-client route
  const isClient = isClientView();
  if (isClient && !clientOnly) {
    // Check if current route is in client-accessible routes
    const isClientRoute = CLIENT_ACCESSIBLE_ROUTES.some((route) =>
      location.pathname.startsWith(route)
    );

    if (!isClientRoute) {
      // Redirect client_view users to client overview if they try to access non-client routes
      return <Navigate to="/client-overview" replace />;
    }
  }

  // Check role requirement (case-insensitive)
  if (
    requiredRole &&
    user?.role?.toLowerCase() !== requiredRole.toLowerCase()
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check admin requirement
  if (requiredRole?.toLowerCase() === "admin" && !isAdmin()) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
